<template>
  <div class="local-space" v-loading="initialLoading" element-loading-text="正在加载任务列表..." element-loading-background="rgba(0, 0, 0, 0.7)">
    <div class="top-alert">
      <el-icon class="notice-icon"><InfoFilled /></el-icon>
      <span>任务完成后，结果文件会存放在云盘空间，请及时下载，任务和文件将在7天后自动删除！<br>查看后台任务状态和进度，停止任务点数也会正常消耗。删除任务不影响结果文件，可在云盘空间查看。</span>
      <div class="auto-refresh-text">
        <el-icon><Refresh /></el-icon>
        <span>自动刷新已启用{{ selectedTasks.length > 0 ? ' - 检测到选中记录，暂停自动刷新' : (hasRunningTasks ? ` - 检测到进行中任务，正在实时更新 (${refreshCountdown}s)` : (hasPendingTasks ? ` - 检测到未开始任务，定期检查 (${refreshCountdown}s)` : ' - 无需要刷新的任务，已停止自动刷新')) }}</span>
      </div>
    </div> 
    
    <!-- 查询条件区域 -->
    <div class="task-query-bar">
      <el-input v-model="title" placeholder="工具名称" clearable style="width: 200px; " @keyup.enter.native="handleSearch" />
      <el-select v-model="status" placeholder="状态" clearable style="width: 140px; ">
        <el-option label="全部" value="" />
        <el-option label="未开始" :value="0" />
        <el-option label="进行中" :value="1" />
        <el-option label="已完成" :value="2" />
        <el-option label="已取消" :value="3" />
        <el-option label="失败" :value="4" />
      </el-select>
      <el-button  @click="handleSearch" >搜索</el-button>
      <el-button icon="Refresh" @click="handleRefresh" >刷新</el-button>
      <el-checkbox 
        :model-value="isAllSelected" 
        @change="handleSelectAll"
        style="margin-right: 24px;"
      >
        全选
      </el-checkbox>
      <el-button type="danger" @click="handleBatchDelete" :disabled="selectedTasks.length === 0">批量删除</el-button>
      <el-button type="primary" @click="handleBatchDownload" :disabled="selectedTasks.length === 0" >批量下载</el-button>
    </div>
    <!-- 主内容区域：左右分区 -->
    <div class="local-space-main">
      
      <div class="file-list-area">
        <div class="task-list-scrollable">
          <!-- 骨架屏 -->
          <div v-if="loading || dataProcessing" class="task-card-grid">
            <el-card
              v-for="n in limit"
              :key="`skeleton-${n}`"
              class="task-card skeleton-card"
            >
              <el-skeleton :loading="true" animated>
                <template #template>
                  <div class="skeleton-thumbnail"></div>
                  <div class="skeleton-content">
                    <el-skeleton-item variant="text" style="width: 60%; margin-bottom: 12px;" />
                    <el-skeleton-item variant="text" style="width: 40%; margin-bottom: 8px;" />
                    <el-skeleton-item variant="text" style="width: 50%;" />
                  </div>
                  <div class="skeleton-actions">
                    <el-skeleton-item variant="button" style="width: 60px; margin-right: 8px;" />
                    <el-skeleton-item variant="button" style="width: 60px; margin-right: 8px;" />
                    <el-skeleton-item variant="button" style="width: 60px;" />
                  </div>
                </template>
              </el-skeleton>
            </el-card>
          </div>
          <!-- 实际内容 -->
          <div v-else-if="tasktList.length" class="task-card-grid">
            <el-card
              v-for="task in tasktList"
              :key="task.raw.id"
              class="task-card"
              :class="`task-card-${task.status}`"
            >
              <input 
                type="checkbox" 
                class="task-card-checkbox"
                :checked="isTaskSelected(task)"
                @click.stop
                @change="toggleTaskSelection(task, $event.target.checked)"
              />

              <div
                class="task-card-thumbnail"
                :class="{ 'thumbnail-clickable': !!task.resultUrl }"
                @click="handleThumbnailClick(task)"
              >
                <div v-if="getTaskPreviewType(task) === 'other'" class="task-thumbnail-icon">
                  <el-icon :size="48">
                    <Document/>
                  </el-icon>
                </div>
                <img
                  v-if="getTaskPreviewType(task) === 'audio'"
                  :src="waveImage"
                  alt="audio preview"
                  class="task-thumbnail-audio"
                  loading="lazy"
                />
                <video
                  v-if="getTaskPreviewType(task) === 'video' && task.resultUrl"
                  :ref="el => setVideoRef(el, task.raw.id)"
                  :src="task.resultUrl"
                  class="task-thumbnail-video"
                  muted
                  :preload="getVideoPreload(task.raw.id)"
                  playsinline
                  @error="handleVideoError($event, task)"
                  @loadedmetadata="handleVideoLoadedMetadata($event, task)"
                  @loadeddata="handleVideoLoadedData($event, task)"
                  @canplay="handleVideoCanPlay($event, task)"
                  crossorigin="anonymous"
                />
                <div v-if="Number(task.queue) > 0" class="queue-badge">
                  排队 {{ task.queue }}
                </div>
                
                <i 
                  v-if="task.status === 'success' && ['audio', 'video'].includes(getTaskPreviewType(task))"
                  class="fa-regular fa-circle-play thumbnail-play-button"
                  @click.stop="handleThumbnailClick(task)"
                ></i>
                <div v-if="task.status !== 'success'" class="task-progress-overlay">
                  
                  <el-progress
                    :percentage="task.progress"
                    :status="task.status"
                    :stroke-width="4"
                    :show-text="false"
                  />
                </div>
              </div>

              <div class="task-card-content">
                <div class="task-card-name" :title="task.name">
                  <div class="meta-item">
                    <el-icon class="meta-icon"><Postcard/></el-icon>
                    <span class="value">{{ task.name || '-' }}</span>
                  </div>
                  <div class="task-card-name-right">
                    <el-tooltip
                      v-if="task.status !== 'success' && task.remark"
                      effect="dark"
                      :content="task.remark"
                      placement="top"
                    >
                      <el-icon class="status-info-icon"><InfoFilled /></el-icon>
                    </el-tooltip>
                    <el-tag size="small" :type="getStatusTagType(task.raw.status)">
                      {{ getStatusText(task.raw.status) }}
                    </el-tag>
                  </div>
                  
                </div>
                <div class="task-card-meta">
                  <div class="meta-item">
                    <el-icon class="meta-icon"><Setting/></el-icon>
                    <span class="value">{{ task.tool || '-' }}</span>
                  </div>
                  <div class="meta-item">
                    <el-icon class="meta-icon"><Clock/></el-icon>
                    <span class="value">{{ task.startTime || '-' }}</span>
                  </div>
                  <div class="meta-item">
                    <el-icon class="meta-icon"><Coin/></el-icon>
                    <span class="value">{{ task.power }}</span>
                  </div>
                </div>
              </div>

              <div class="task-card-actions">
                <el-button
                  v-if="[0, 1, 4].includes(task.raw.status)"
                  type="warning"
                  :icon="Refresh"
                  size="small"
                  text
                  @click="handleRetryTask(task.raw)"
                >
                  重试
                </el-button>
                <el-button
                  type="danger"
                  :icon="Delete"
                  size="small"
                  text
                  @click="handleDeleteTask(task.raw)"
                >
                  删除
                </el-button>
                <el-button
                  type="primary"
                  :icon="Download"
                  size="small"
                  text
                  @click="handleViewResult(task)"
                  :disabled="task.status !== 'success'"
                >
                  下载
                </el-button>
              </div>
            </el-card>
          </div>
          <div v-else class="empty-task-list">
            <el-icon :size="80" color="#555"><View /></el-icon>
            <div class="empty-text">暂无任务数据</div>
          </div>
        </div>

        <el-dialog
          v-model="previewDialogVisible"
          :title="previewTask ? `预览 - ${previewTask.name}` : '任务预览'"
          width="640px"
          :close-on-click-modal="false"
          center
          @close="handlePreviewClose"
        >
          <div v-if="previewTask" class="task-preview-content">
            <div v-if="previewType === 'video'" class="task-video-preview">
              <video
                :src="previewTask.resultUrl"
                controls
                preload="metadata"
                style="width: 100%; max-height: 380px;"
              />
            </div>
            <div v-else-if="previewType === 'audio'" class="task-audio-preview">
              <audio
                :src="previewTask.resultUrl"
                controls
                preload="metadata"
                style="width: 100%;"
              />
            </div>
            <div v-else class="preview-placeholder">
              <el-icon :size="60" color="#555"><Document /></el-icon>
              <p>该任务结果不支持预览，请直接下载。</p>
            </div>
          </div>
          <template #footer>
            <el-button @click="previewDialogVisible = false">关闭</el-button>
            <el-button
              type="primary"
              :icon="Download"
              :disabled="!(previewTask && previewTask.resultUrl)"
              @click="handleViewResult(previewTask)"
            >
              下载文件
            </el-button>
          </template>
        </el-dialog>
        <!-- 分页控件 -->
        <Pagination
          :total="total"
          :current-page="page"
          :page-size="limit"
          :page-sizes="[50, 100, 200]"
          @page-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useUserStore } from '@/stores/portal/user'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Delete, Edit, Refresh, View, Document, Download, Headset, Lightning, InfoFilled, Clock } from '@element-plus/icons-vue'
// 导入API请求函数
import {deleteTask, batchDeleteUserTasks, getAiToolsTasksListByPage } from '@/api/portal/aitools'
import { batchDownloadFile } from '@/api/portal/aitools'
import { updateTask } from '@/api/portal/aitools'
import waveImage from '@/apps/portal/assets/wave.png'
import Pagination from '@/apps/portal/components/Pagination.vue'

const tasktList = ref([])
const total = ref(0)
const page = ref(1)
const limit = ref(50)
const status = ref(undefined)
const title = ref('')
const loading = ref(false)
const dataProcessing = ref(false) // 数据正在处理中
const initialLoading = ref(false) // 首次加载状态
const userStore = useUserStore()
const refreshTimer = ref(null)
const pendingTimer = ref(null)
// 添加选中任务跟踪
const selectedTasks = ref([])
// 智能刷新相关状态
const hasRunningTasks = ref(false)
const hasPendingTasks = ref(false)
// 倒计时相关状态
const refreshCountdown = ref(0)
const countdownInterval = ref(null)
const refreshInterval = 5000 // 刷新间隔（毫秒）
const pendingInterval = 30000 // 未开始任务刷新间隔（毫秒）
const previewDialogVisible = ref(false)
const previewTask = ref(null)
const previewType = ref('')

// 视频懒加载相关
const videoRefs = new Map()
const loadedVideos = ref(new Set())
let videoObserver = null

// 初始化视频 Intersection Observer
const initVideoObserver = () => {
  if (videoObserver) {
    // console.log('[BackgroundTask] 视频 Observer 已存在，跳过初始化')
    return
  }
  
  // console.log('[BackgroundTask] 创建视频 Intersection Observer')
  videoObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const videoId = entry.target.dataset.videoId
        if (videoId && !loadedVideos.value.has(videoId)) {
          // console.log('[BackgroundTask] 视频进入视口，开始加载元数据:', videoId)
          loadedVideos.value.add(videoId)
          const video = entry.target
          if (video) {
            // 设置为auto以加载第一帧
            video.preload = 'auto'
            // 确保定位到第一帧
            video.currentTime = 0.1 // 设置为0.1秒，避免某些视频第一帧是黑屏
          }
        }
      }
    })
  }, {
    rootMargin: '50px' // 提前50px开始加载
  })
  // console.log('[BackgroundTask] 视频 Intersection Observer 创建完成')
}

// 设置视频引用
const setVideoRef = (el, taskId) => {
  if (el && taskId) {
    el.dataset.videoId = taskId
    videoRefs.set(taskId, el)
    
    // 初始化 Observer
    if (!videoObserver) {
      initVideoObserver()
    }
    
    // 观察视频元素
    if (videoObserver) {
      videoObserver.observe(el)
    }
  }
}

// 获取视频 preload 值
const getVideoPreload = (taskId) => {
  // 如果视频已经进入视口，使用auto以加载第一帧
  return loadedVideos.value.has(taskId) ? 'auto' : 'none'
}

// 处理视频加载错误
const handleVideoError = (event, task) => {
  const video = event.target
  console.error('[BackgroundTask] 视频加载失败:', {
    taskId: task.raw.id,
    resultUrl: task.resultUrl,
    error: video.error ? {
      code: video.error.code,
      message: video.error.message
    } : null,
    networkState: video.networkState,
    readyState: video.readyState
  })
  
  // 如果视频加载失败，可能是Content-Disposition设置为attachment导致的
  // 建议在腾讯云COS控制台将Content-Disposition改为inline
  if (video.error && video.error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
    console.warn('[BackgroundTask] 视频格式不支持或Content-Type/Content-Disposition配置错误')
    console.warn('[BackgroundTask] 建议检查：1. Content-Type是否为video/mp4 2. Content-Disposition是否为inline（不是attachment）')
  }
}

// 处理视频元数据加载成功
const handleVideoLoadedMetadata = (event, task) => {
  const video = event.target
  console.log('[BackgroundTask] 视频元数据加载成功:', {
    taskId: task.raw.id,
    videoWidth: video.videoWidth,
    videoHeight: video.videoHeight,
    duration: video.duration
  })
  
  // 确保定位到第一帧（0.1秒，避免某些视频第一帧是黑屏）
  if (video.readyState >= 1) { // HAVE_METADATA
    video.currentTime = 0.1
  }
}

// 处理视频第一帧数据加载成功
const handleVideoLoadedData = (event, task) => {
  const video = event.target
  console.log('[BackgroundTask] 视频第一帧数据加载成功:', {
    taskId: task.raw.id,
    currentTime: video.currentTime,
    readyState: video.readyState
  })
  
  // 确保定位到第一帧
  if (video.currentTime === 0 || video.currentTime < 0.1) {
    video.currentTime = 0.1
  }
}

// 处理视频可以播放
const handleVideoCanPlay = (event, task) => {
  const video = event.target
  console.log('[BackgroundTask] 视频可以播放:', {
    taskId: task.raw.id,
    currentTime: video.currentTime,
    readyState: video.readyState
  })
  
  // 确保定位到第一帧并暂停（不自动播放）
  if (video.currentTime === 0 || video.currentTime < 0.1) {
    video.currentTime = 0.1
  }
  video.pause() // 确保视频暂停，只显示第一帧
}

// 清理不再存在的视频观察
const cleanupUnusedVideoObservers = (currentTaskIds) => {
  if (!videoObserver) return
  
  const currentIds = new Set(currentTaskIds)
  const toRemove = []
  
  videoRefs.forEach((el, taskId) => {
    if (!currentIds.has(taskId)) {
      toRemove.push(taskId)
      videoObserver.unobserve(el)
    }
  })
  
  toRemove.forEach(taskId => {
    videoRefs.delete(taskId)
    loadedVideos.value.delete(taskId)
  })
}

// 清理视频 Observer
const cleanupVideoObserver = () => {
  if (videoObserver) {
    videoRefs.forEach((el) => {
      videoObserver.unobserve(el)
    })
    videoRefs.clear()
    loadedVideos.value.clear()
    videoObserver.disconnect()
    videoObserver = null
  }
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

// 获取任务状态文本
const getStatusText = (status) => {
  const statusMap = {
    0: '未开始',
    1: '进行中',
    2: '已完成',
    3: '已取消',
    4: '失败'
  }
  return statusMap[status] || '未知'
}

const getStatusTagType = (status) => {
  const map = {
    0: 'primary',
    1: 'warning',
    2: 'success',
    3: 'info',
    4: 'danger'
  }
  return map[status] || 'info'
}

// 获取任务状态样式
const getStatusStyle = (status) => {
  switch(status) {
    case 1: return 'running'
    case 2: return 'success'
    case 3: return 'canceled'
    case 4: return 'exception'
    default: return ''
  }
}

// 获取任务进度数值
const getProgressValue = (progress) => {
  if (!progress) return 0
  if (typeof progress === 'string') {
    const num = parseInt(progress)
    return isNaN(num) ? 0 : num
  }
  return progress
}

const getTaskDisplayName = (item) => {
  let parameters = null
  try {
    parameters = typeof item.parameters === 'string'
      ? JSON.parse(item.parameters)
      : item.parameters
  } catch (error) {
    console.warn('解析后台任务标题参数失败:', error)
  }

  if (parameters?.taskTitle) return parameters.taskTitle

  const projectName = parameters?.projectName
  if (projectName && Number(item.toolId) === 63) {
    return `数字人视频（${projectName}）`
  }
  if (projectName && Number(item.toolId) === 7) {
    return `视频混剪（${projectName}）`
  }
  return item.taskId
}

// 分批处理数据，避免阻塞主线程
const processTaskListInBatches = async (rawList, batchSize = 10) => {
  // console.log('[BackgroundTask] 开始分批处理数据，总数:', rawList.length, '每批:', batchSize)
  const processedList = []
  
  for (let i = 0; i < rawList.length; i += batchSize) {
    const batch = rawList.slice(i, i + batchSize)
    // const batchStartTime = performance.now()
    
    // 处理当前批次
    const processedBatch = batch.map(item => ({
      name: getTaskDisplayName(item),
      tool: item.toolName,
      remark: item.remark,
      status: getStatusStyle(item.status),
      statusCode: item.status,
      progress: getProgressValue(item.progress),
      startTime: formatDate(item.startTime),
      finishTime: formatDate(item.finishTime),
      createDate: formatDate(item.createDate),
      duration: item.duration ? `${item.duration}s` : '-',
      power: item.computingPower || 0,
      queue: item.queue || '-',
      resultUrl: item.resultUrl,
      raw: item
    }))
    
    processedList.push(...processedBatch)
    // const batchEndTime = performance.now()
    // console.log(`[BackgroundTask] 批次 ${Math.floor(i / batchSize) + 1} 处理完成，耗时: ${(batchEndTime - batchStartTime).toFixed(2)} ms`)
    
    // 使用 requestIdleCallback 或 setTimeout 让出主线程
    if (i + batchSize < rawList.length) {
      await new Promise(resolve => {
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => resolve(), { timeout: 100 })
        } else {
          setTimeout(() => resolve(), 0)
        }
      })
    }
  }
  
  // console.log('[BackgroundTask] 所有批次处理完成，总数:', processedList.length)
  return processedList
}

// 分批处理数据并逐步渲染
const processTaskListInBatchesAndRender = async (rawList, batchSize = 10) => {
  // console.log('[BackgroundTask] 开始分批处理并逐步渲染数据，总数:', rawList.length, '每批:', batchSize)
  // const dataProcessStartTime = performance.now()
  
  for (let i = 0; i < rawList.length; i += batchSize) {
    const batch = rawList.slice(i, i + batchSize)
    // const batchStartTime = performance.now()
    
    // 处理当前批次
    const processedBatch = batch.map(item => ({
      name: getTaskDisplayName(item),
      tool: item.toolName,
      remark: item.remark,
      status: getStatusStyle(item.status),
      statusCode: item.status,
      progress: getProgressValue(item.progress),
      startTime: formatDate(item.startTime),
      finishTime: formatDate(item.finishTime),
      createDate: formatDate(item.createDate),
      duration: item.duration ? `${item.duration}s` : '-',
      power: item.computingPower || 0,
      queue: item.queue || '-',
      resultUrl: item.resultUrl,
      raw: item
    }))
    
    // 逐步添加到列表
    tasktList.value.push(...processedBatch)
    
    // const batchEndTime = performance.now()
    // console.log(`[BackgroundTask] 批次 ${Math.floor(i / batchSize) + 1} 处理并渲染完成，耗时: ${(batchEndTime - batchStartTime).toFixed(2)} ms`)
    
    // 使用 requestIdleCallback 或 setTimeout 让出主线程
    if (i + batchSize < rawList.length) {
      await new Promise(resolve => {
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => resolve(), { timeout: 50 })
        } else {
          setTimeout(() => resolve(), 0)
        }
      })
    }
  }
  
  // const dataProcessEndTime = performance.now()
  // console.log('[BackgroundTask] 所有批次处理并渲染完成，总耗时:', (dataProcessEndTime - dataProcessStartTime).toFixed(2), 'ms', '总数:', tasktList.value.length)
  
  // 数据处理完成，取消数据处理状态
  dataProcessing.value = false
  
  // 首次加载完成，关闭全屏 loading
  if (initialLoading.value) {
    // console.log('[BackgroundTask] 首次加载完成，关闭全屏 loading')
    initialLoading.value = false
  }
  
  // 检查任务状态
  // console.log('[BackgroundTask] 检查任务状态')
  checkTaskStatus()
  
  // 清理不再存在的视频观察
  // console.log('[BackgroundTask] 清理视频观察')
  const currentTaskIds = tasktList.value
    .filter(task => getTaskPreviewType(task) === 'video' && task.resultUrl)
    .map(task => task.raw.id)
  cleanupUnusedVideoObservers(currentTaskIds)
}

// 加载任务列表（分页）
const loadTaskList = async (preserveSelection = false) => {
  // console.log('[BackgroundTask] loadTaskList 开始', { preserveSelection, page: page.value, limit: limit.value })
  // const startTime = performance.now()
  
  // 只有在非自动刷新时才显示 loading
  if (!preserveSelection) {
    // console.log('[BackgroundTask] 设置 loading = true')
    loading.value = true
    // 如果是首次加载（列表为空），显示全屏 loading
    if (tasktList.value.length === 0) {
      // console.log('[BackgroundTask] 首次加载，显示全屏 loading')
      initialLoading.value = true
    }
  }
  try {
    const params = {
      page: page.value,
      limit: limit.value,
    }
    if (status.value !== undefined && status.value !== null && status.value !== '') params.status = status.value
    if (title.value) params.title = title.value
    
    // console.log('[BackgroundTask] 准备调用 API，参数:', params)
    // const apiStartTime = performance.now()
    const res = await getAiToolsTasksListByPage(params)
    // const apiEndTime = performance.now()
    // console.log('[BackgroundTask] API 调用完成，耗时:', (apiEndTime - apiStartTime).toFixed(2), 'ms', '返回数据:', res)
    if (res && res.page) {
      const rawList = res.page.list || []
      // console.log('[BackgroundTask] 开始异步处理数据，任务数量:', rawList.length)
      
      // 设置数据处理状态，继续显示 skeleton
      if (!preserveSelection) {
        dataProcessing.value = true
        // 先清空列表，准备显示新数据
        tasktList.value = []
        total.value = res.page.total || 0
      }
      
      // 异步分批处理数据并逐步显示
      // const dataProcessStartTime = performance.now()
      
      if (!preserveSelection) {
        // 非自动刷新时，分批处理并逐步显示（processTaskListInBatchesAndRender 内部会处理所有逻辑）
        await processTaskListInBatchesAndRender(rawList, 10)
      } else {
        // 自动刷新时，一次性处理所有数据
        const newTaskList = await processTaskListInBatches(rawList, 10)
        // const dataProcessEndTime = performance.now()
        // console.log('[BackgroundTask] 数据处理完成，耗时:', (dataProcessEndTime - dataProcessStartTime).toFixed(2), 'ms', '处理后的任务数量:', newTaskList.length)
        
        // 增量更新
        // console.log('[BackgroundTask] 执行增量更新')
        // const updateStartTime = performance.now()
        const hasChanges = updateTaskListIncremental(newTaskList)
        // const updateEndTime = performance.now()
        // console.log('[BackgroundTask] 增量更新完成，耗时:', (updateEndTime - updateStartTime).toFixed(2), 'ms', '是否有变化:', hasChanges)
        
        // 只有在有变化时才更新总数和检查状态，减少不必要的计算
        if (hasChanges) {
          total.value = res.page.total || 0
          // console.log('[BackgroundTask] 检查任务状态')
          checkTaskStatus()
          // 清理不再存在的视频观察
          // console.log('[BackgroundTask] 清理视频观察')
          const currentTaskIds = tasktList.value
            .filter(task => getTaskPreviewType(task) === 'video' && task.resultUrl)
            .map(task => task.raw.id)
          cleanupUnusedVideoObservers(currentTaskIds)
        }
      }
      
      // const endTime = performance.now()
      // console.log('[BackgroundTask] loadTaskList 完成，总耗时:', (endTime - startTime).toFixed(2), 'ms')
    } else {
      tasktList.value = []
      total.value = 0
      hasRunningTasks.value = false
      hasPendingTasks.value = false
      dataProcessing.value = false
    }
  } catch (error) {
    // console.error('[BackgroundTask] loadTaskList 出错:', error)
    ElMessage.error('加载任务列表失败')
    tasktList.value = []
    total.value = 0
    hasRunningTasks.value = false
    hasPendingTasks.value = false
    dataProcessing.value = false
    // 出错时也要关闭全屏 loading
    initialLoading.value = false
  } finally {
    // console.log('[BackgroundTask] 设置 loading = false')
    loading.value = false
    // 确保全屏 loading 已关闭（防止某些情况下未关闭）
    if (tasktList.value.length > 0) {
      initialLoading.value = false
    }
    // const finalEndTime = performance.now()
    // console.log('[BackgroundTask] loadTaskList 最终完成，总耗时:', (finalEndTime - startTime).toFixed(2), 'ms')
  }
}

// 检查任务数据是否有变化
const hasTaskChanged = (oldTask, newTask) => {
  if (!oldTask || !newTask) return true
  // 只比较关键字段，减少不必要的更新
  return (
    oldTask.raw.status !== newTask.raw.status ||
    oldTask.progress !== newTask.progress ||
    oldTask.raw.progress !== newTask.raw.progress ||
    oldTask.raw.queue !== newTask.raw.queue ||
    oldTask.raw.resultUrl !== newTask.raw.resultUrl ||
    oldTask.raw.remark !== newTask.raw.remark
  )
}

// 增量更新任务列表，保持勾选状态，只更新变化的任务
const updateTaskListIncremental = (newTaskList) => {
  // 创建新任务列表的映射，以taskId为key
  const newTaskMap = new Map()
  newTaskList.forEach(task => {
    newTaskMap.set(task.raw.id, task)
  })
  
  let hasChanges = false
  
  // 更新现有任务列表，保持勾选状态，只更新变化的任务
  tasktList.value = tasktList.value.map(existingTask => {
    const newTask = newTaskMap.get(existingTask.raw.id)
    if (newTask) {
      // 检查任务是否有变化
      if (hasTaskChanged(existingTask, newTask)) {
        hasChanges = true
        return {
          ...newTask,
        }
      }
      // 如果任务没有变化，返回原任务对象（保持引用，避免重新渲染）
      return existingTask
    }
    // 任务被删除
    hasChanges = true
    return existingTask
  })
  
  // 添加新任务（如果有的话）
  const existingTaskIds = new Set(tasktList.value.map(task => task.raw.id))
  const newTasks = newTaskList.filter(task => !existingTaskIds.has(task.raw.id))
  if (newTasks.length > 0) {
    hasChanges = true
    tasktList.value.push(...newTasks)
  }
  
  // 移除已删除的任务
  const newTaskIds = new Set(newTaskList.map(task => task.raw.id))
  const beforeLength = tasktList.value.length
  tasktList.value = tasktList.value.filter(task => newTaskIds.has(task.raw.id))
  if (tasktList.value.length !== beforeLength) {
    hasChanges = true
  }
  
  // 如果没有变化，不触发响应式更新
  return hasChanges
}

// 检查任务状态
const checkTaskStatus = () => {
  // console.log('[BackgroundTask] checkTaskStatus 开始')
  // const checkStartTime = performance.now()
  
  const runningTasks = tasktList.value.filter(task => task.raw.status === 1)
  const pendingTasks = tasktList.value.filter(task => task.raw.status === 0)
  
  const hadRunningTasks = hasRunningTasks.value
  const hadPendingTasks = hasPendingTasks.value
  
  hasRunningTasks.value = runningTasks.length > 0
  hasPendingTasks.value = pendingTasks.length > 0
  
  // console.log('[BackgroundTask] 任务状态检查结果:', {
  //   进行中任务数: runningTasks.length,
  //   未开始任务数: pendingTasks.length,
  //   hasRunningTasks: hasRunningTasks.value,
  //   hasPendingTasks: hasPendingTasks.value,
  //   状态变化: hadRunningTasks !== hasRunningTasks.value || hadPendingTasks !== hasPendingTasks.value
  // })
  
  // 如果任务状态发生变化，重新启动刷新和倒计时
  if (hadRunningTasks !== hasRunningTasks.value || hadPendingTasks !== hasPendingTasks.value) {
    // console.log('[BackgroundTask] 任务状态发生变化，重新启动智能刷新')
    startSmartRefresh()
  }
  
  // const checkEndTime = performance.now()
  // console.log('[BackgroundTask] checkTaskStatus 完成，耗时:', (checkEndTime - checkStartTime).toFixed(2), 'ms')
}

// 启动倒计时
const startCountdown = () => {
  // 清除现有倒计时
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value)
  }
  
  // 根据任务状态设置倒计时
  let interval
  if (hasRunningTasks.value) {
    interval = refreshInterval
  } else if (hasPendingTasks.value) {
    interval = pendingInterval
  } else {
    return // 没有需要刷新的任务
  }
  
  refreshCountdown.value = Math.ceil(interval / 1000)
  
  // 启动倒计时定时器
  countdownInterval.value = setInterval(() => {
    refreshCountdown.value--
    if (refreshCountdown.value <= 0) {
      // 重新计算倒计时间隔
      let currentInterval
      if (hasRunningTasks.value) {
        currentInterval = refreshInterval
      } else if (hasPendingTasks.value) {
        currentInterval = pendingInterval
      } else {
        stopCountdown()
        return
      }
      refreshCountdown.value = Math.ceil(currentInterval / 1000)
    }
  }, 1000)
}

// 停止倒计时
const stopCountdown = () => {
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value)
    countdownInterval.value = null
  }
  refreshCountdown.value = 0
}

// 智能刷新逻辑
const startSmartRefresh = () => {
  // console.log('[BackgroundTask] startSmartRefresh 开始', {
  //   hasRunningTasks: hasRunningTasks.value,
  //   hasPendingTasks: hasPendingTasks.value
  // })
  
  // 清除现有定时器
  stopAllRefreshTimers()
  
  // 根据任务状态启动相应的刷新
  if (hasRunningTasks.value) {
    // 有进行中任务时，5秒刷新
    // console.log('[BackgroundTask] 启动进行中任务刷新（5秒间隔）')
    startCountdown()
    startNormalRefresh()
  } else if (hasPendingTasks.value) {
    // 只有未开始任务时，30秒刷新
    // console.log('[BackgroundTask] 启动未开始任务刷新（30秒间隔）')
    startCountdown()
    startPendingRefresh()
  } else {
    // 没有任何需要刷新的任务时停止倒计时
    // console.log('[BackgroundTask] 没有需要刷新的任务，停止刷新')
    stopCountdown()
  }
  
  // console.log('[BackgroundTask] startSmartRefresh 完成')
}

// 启动正常频率刷新（只刷新进行中的任务）
const startNormalRefresh = () => {
  if (refreshTimer.value) {
    clearInterval(refreshTimer.value)
  }
  
  refreshTimer.value = setInterval(() => {
    // 如果有选中记录，暂停刷新
    if (selectedTasks.value.length > 0) {
      return
    }
    
    // 检查当前页面是否还有进行中的任务
    const currentRunningTasks = tasktList.value.filter(task => task.raw.status === 1)
    if (currentRunningTasks.length === 0) {
      // 当前页面没有进行中任务，停止刷新
      stopAllRefreshTimers()
      checkTaskStatus() // 重新检查任务状态
      startSmartRefresh() // 重新启动智能刷新
      return
    }
    
    // 只刷新有进行中任务的页面
    loadTaskList(true) // 自动刷新时保持勾选状态，使用增量更新
  }, refreshInterval)
}

// 启动未开始任务刷新（降低刷新频率）
const startPendingRefresh = () => {
  if (pendingTimer.value) {
    clearInterval(pendingTimer.value)
  }
  
  pendingTimer.value = setInterval(() => {
    // 如果有选中记录，暂停刷新
    if (selectedTasks.value.length > 0) {
      return
    }
    
    // 检查当前页面是否还有未开始的任务
    const currentPendingTasks = tasktList.value.filter(task => task.raw.status === 0)
    if (currentPendingTasks.length === 0) {
      // 当前页面没有未开始任务，停止刷新
      stopAllRefreshTimers()
      checkTaskStatus() // 重新检查任务状态
      startSmartRefresh() // 重新启动智能刷新
      return
    }
    
    // 检查是否有进行中任务（优先级更高）
    if (hasRunningTasks.value) {
      stopAllRefreshTimers()
      startSmartRefresh()
      return
    }
    
    loadTaskList(true) // 自动刷新时保持勾选状态，使用增量更新
  }, pendingInterval)
}


// 停止所有刷新定时器
const stopAllRefreshTimers = () => {
  if (refreshTimer.value) {
    clearInterval(refreshTimer.value)
    refreshTimer.value = null
  }
  if (pendingTimer.value) {
    clearInterval(pendingTimer.value)
    pendingTimer.value = null
  }
}

// 停止智能刷新
const stopSmartRefresh = () => {
  stopAllRefreshTimers()
  stopCountdown()
}

// 重启智能刷新（当有新的进行中任务时）
const restartSmartRefresh = () => {
  startSmartRefresh()
}

// 添加耗时计算函数
const calculateDuration = (startTime, finishTime, status) => {
  if (!startTime) return '-';

  const start = new Date(startTime);
  let end;

  // 根据任务状态确定结束时间
  if ([2, 3, 4].includes(status)) {
    // 已完成、已取消、失败状态使用finishTime
    end = finishTime ? new Date(finishTime) : new Date();
  } else if (status === 1) {
    // 进行中状态使用当前时间
    end = new Date();
  } else {
    // 未开始状态
    return '-';
  }

  // 计算时间差（毫秒）
  const diffMs = end - start;
  if (diffMs < 0) return '-';

  // 转换为秒、分、小时
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  // 格式化显示
  if (hours > 0) {
    return `${hours}h${minutes % 60}m${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

onMounted(() => {
  // console.log('[BackgroundTask] onMounted 开始')
  // const mountStartTime = performance.now()
  
  // 设置页面标题
  document.title = '后台任务'
  // console.log('[BackgroundTask] 页面标题设置完成')

  // 初始化视频 Observer
  // console.log('[BackgroundTask] 初始化视频 Observer')
  // const observerStartTime = performance.now()
  initVideoObserver()
  // const observerEndTime = performance.now()
  // console.log('[BackgroundTask] 视频 Observer 初始化完成，耗时:', (observerEndTime - observerStartTime).toFixed(2), 'ms')

  // console.log('[BackgroundTask] 开始加载任务列表')
  loadTaskList(false) // 初始加载时不保持勾选状态
  
  // 启动智能定时刷新
  // console.log('[BackgroundTask] 启动智能定时刷新')
  startSmartRefresh()
  
  // const mountEndTime = performance.now()
  // console.log('[BackgroundTask] onMounted 完成，总耗时:', (mountEndTime - mountStartTime).toFixed(2), 'ms')
})

onBeforeUnmount(() => {
  // 清除智能刷新定时器
  stopSmartRefresh()
  // 清理视频 Observer
  cleanupVideoObserver()
})

// 修改操作按钮事件处理函数
const handleViewResult = async (row) => {
  // 检查是否为混剪任务（tool中包含"07"）
  if (row.tool && row.tool.includes('07')) {
    ElMessageBox.alert('混剪任务请到【云盘空间】下载', '提示', {
      confirmButtonText: '确定',
      type: 'info'
    })
    return
  }
  
  if (!row.resultUrl) {
    ElMessage.warning('暂无结果文件')
    return
  }
  
  try {
    // 使用fetch下载文件，确保即使Content-Disposition是inline也能强制下载
    ElMessage.info('正在准备下载...')
    
    const response = await fetch(row.resultUrl, {
      method: 'GET',
      headers: {
        'Accept': '*/*'
      }
    })
    
    if (!response.ok) {
      throw new Error(`下载失败: ${response.status} ${response.statusText}`)
    }
    
    // 获取文件blob
    const blob = await response.blob()
    
    // 从URL中提取文件名，如果没有则使用默认名称
    const url = new URL(row.resultUrl)
    const pathParts = url.pathname.split('/')
    let fileName = pathParts[pathParts.length - 1] || 'download'
    
    // 如果文件名没有扩展名，尝试从Content-Type推断
    if (!fileName.includes('.')) {
      const contentType = response.headers.get('Content-Type')
      if (contentType) {
        const extension = contentType.split('/')[1]?.split(';')[0]
        if (extension) {
          fileName = `${fileName}.${extension}`
        }
      }
    }
    
    // 创建下载链接
    const blobUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = fileName
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // 释放blob URL
    window.URL.revokeObjectURL(blobUrl)
    
    ElMessage.success('下载已开始')
  } catch (error) {
    console.error('下载失败:', error)
    ElMessage.error('下载失败: ' + (error.message || '未知错误'))
    // 如果fetch失败，回退到直接打开链接（虽然可能会播放而不是下载）
    // window.open(row.resultUrl, '_blank')
  }
}

const handleDeleteTask = async (task) => {
  try {
    // 显示确认对话框
    const confirmResult = await ElMessageBox.confirm(
      '确定要删除该任务吗？此操作不可恢复。',
      '删除确认',
      {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )

    // 用户点击确认后执行删除
    if (confirmResult === 'confirm') {
      await deleteTask(task.id)
      ElMessage.success('任务删除成功')
      loadTaskList(false) // 删除后不保持勾选状态
    }
  } catch (error) {
    // 用户取消操作或发生错误时不执行删除
    if (error !== 'cancel') {
      ElMessage.error('删除任务失败')
      // console.error(error)
    }
  }
}

// 添加重试任务处理函数
const handleRetryTask = async (task) => {
  try {
    // 显示确认对话框
    const confirmResult = await ElMessageBox.confirm(
      '确定要重试该任务吗？',
      '重试确认',
      {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )

    // 用户点击确认后执行重试
    if (confirmResult === 'confirm') {
      await updateTask(task.id, 0)
      ElMessage.success('任务重试成功')
      loadTaskList(false) // 重试后不保持勾选状态
      // 重启智能刷新，因为可能有新的进行中任务
      restartSmartRefresh()
    }
  } catch (error) {
    // 用户取消操作或发生错误时不执行重试
    if (error !== 'cancel') {
      ElMessage.error('重试任务失败')
      // console.error(error)
    }
  }
}

// 添加选中变更处理函数
const handleSelectionChange = (selection) => {
  selectedTasks.value = selection
  // 当选中状态变化时，重新启动智能刷新
  if (selection.length === 0) {
    // 如果没有选中项，重新启动刷新
    restartSmartRefresh()
  }
  // 如果有选中项，智能刷新逻辑会自动暂停（在 startSmartRefresh 中处理）
}

function isTaskSelected(task) {
  return selectedTasks.value.some(selected => selected.raw.id === task.raw.id)
}

function toggleTaskSelection(task, checked) {
  let nextSelection = [...selectedTasks.value]
  if (checked) {
    if (!isTaskSelected(task)) {
      nextSelection.push(task)
    }
  } else {
    nextSelection = nextSelection.filter(selected => selected.raw.id !== task.raw.id)
  }
  handleSelectionChange(nextSelection)
}

// 判断是否全选（当前页面的所有任务都被选中）
const isAllSelected = computed(() => {
  if (tasktList.value.length === 0) return false
  return tasktList.value.every(task => isTaskSelected(task))
})

// 处理全选/取消全选
function handleSelectAll(checked) {
  if (checked) {
    // 全选：将当前页面所有未选中的任务添加到选中列表
    const tasksToAdd = tasktList.value.filter(task => !isTaskSelected(task))
    handleSelectionChange([...selectedTasks.value, ...tasksToAdd])
  } else {
    // 取消全选：从选中列表中移除当前页面的所有任务
    const currentPageTaskIds = tasktList.value.map(task => task.raw.id)
    const newSelection = selectedTasks.value.filter(
      task => !currentPageTaskIds.includes(task.raw.id)
    )
    handleSelectionChange(newSelection)
  }
}


function getTaskPreviewType(task) {
  if (!task?.resultUrl) return 'other'
  const url = task.resultUrl.split('?')[0]
  const extension = url.split('.').pop()?.toLowerCase() || ''
  const audioExtensions = ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a', 'wma', 'opus']
  const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v', '3gp', 'ogv']
  if (audioExtensions.includes(extension)) return 'audio'
  if (videoExtensions.includes(extension)) return 'video'
  return 'other'
}

function handleThumbnailClick(task) {
  if (!task.resultUrl) {
    ElMessage.info('暂无可预览的结果')
    return
  }
  if (task.status !== 'success') {
    ElMessage.info('任务未完成，无法预览')
    return
  }
  previewTask.value = task
  previewType.value = getTaskPreviewType(task)
  previewDialogVisible.value = true
}

function handlePreviewClose() {
  previewDialogVisible.value = false
  previewTask.value = null
  previewType.value = ''
}

const handleBatchDownload = async () => {
  if (selectedTasks.value.length === 0) {
    ElMessage.warning('请选择需要下载的任务')
    return
  }
  if (selectedTasks.value.length > 10) {
    ElMessage.warning('最多选择10个任务')
    return
  }
  // 只取有resultUrl且状态成功的任务
  const downloadableTasks = selectedTasks.value.filter(task => 
    task.resultUrl && task.status === 'success'
  )
  if (downloadableTasks.length === 0) {
    ElMessage.warning('所选任务中没有可下载的结果文件')
    return
  }
  // 假设resultUrl里有fileId，或你有fileId字段
  const fileIds = downloadableTasks.map(task => task.raw.id) // 你要保证raw里有fileId
  if (fileIds.length === 0) {
    ElMessage.warning('所选任务中没有可下载的文件')
    return
  }
  try {
    const res = await batchDownloadFile(fileIds)
    if (res && res.code === 0 && res.url) {
      // 直接下载url
      const link = document.createElement('a')
      link.href = res.url
      link.download = '' // 让浏览器自动用url里的文件名
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      ElMessage.success('批量下载已开始')
    } else {
      ElMessage.error(res && res.msg ? res.msg : '批量下载失败')
    }
  } catch (e) {
    // console.error('批量下载失败:', e, e.response, e.request);
    if (e.request) {
      ElMessage.error('网络错误或服务器无响应');
    } else {
      ElMessage.error('批量下载失败:' + (e && e.message ? e.message : e));
    }
  }
}

const handleBatchDelete = async () => {
  if (selectedTasks.value.length === 0) {
    ElMessage.warning('请先选择需要删除的任务')
    return
  }

  try {
    // 显示确认对话框
    const confirmResult = await ElMessageBox.confirm(
      `确定要删除选中的 ${selectedTasks.value.length} 个任务吗？`,
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )

    if (confirmResult === 'confirm') {
      // 提取选中任务的taskId列表
      const tasksIds = selectedTasks.value.map(task => task.raw.id)

      // 调用批量删除接口
      await batchDeleteUserTasks(tasksIds) // 保持直接传递数组

      ElMessage.success('批量删除成功')
      // 刷新任务列表
      loadTaskList(false) // 批量删除后不保持勾选状态
      // 清空选中状态
      selectedTasks.value = []
    }
  } catch (error) {
    if (error === 'cancel') return // 用户取消操作
    ElMessage.error('批量删除失败')
    // console.error('批量删除错误:', error)
  }
}

function handleSearch() {
  page.value = 1
  loadTaskList(false) // 搜索时不保持勾选状态
}
function handleRefresh() {
  loadTaskList(false) // 手动刷新时不保持勾选状态
  // 手动刷新后重启智能刷新
  restartSmartRefresh()
}
function handlePageChange(val) {
  page.value = val
  loadTaskList(false) // 翻页时不保持勾选状态
}
function handleSizeChange(val) {
  limit.value = val
  page.value = 1
  loadTaskList(false) // 改变页面大小时不保持勾选状态
}
</script>

<style scoped>
/* 顶部提示条 - 黄色警告样式 */
.top-alert {
  background: #FFFBEB;
  border-bottom: 1px solid #FCD34D;
  color: #92400E;
  padding: 10px 20px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.5;
}

.notice-icon {
  color: #F59E0B;
  font-size: 14px;
  flex-shrink: 0;
}

.auto-refresh-text {
  margin-left: auto;
  color: #B45309;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.auto-refresh-text :deep(.el-icon) {
  color: #B45309;
}

.top-btn {
  display: flex;
  margin-top: 12px;
  margin-bottom: 16px;
  width: 100%;
}
.btn-item{
  width: 180px;
  display: flex;
  align-items: center;
  border: 1px solid #E5E7EB;
  border-radius: 20px;
  padding: 12px 12px;
  background-color: #fff;
  margin-right: 12px;
}
.btn-text{
  margin-left: 32px;
  font-size: 18px;
}
.local-space-main {
  display: flex;
  height: calc(100vh - 168px);
  width: 100%;
}
.folder-tree-area {
  max-width: 280px;
  min-width: 250px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255,255,255,0.6);
  border-radius: 16px;
  margin-right: 12px;
}
.file-list-area {
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255,255,255,0.6);
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #fff;
  overflow: hidden;
}

.task-list-scrollable {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  background: #F9FAFB;
}
.custom-task-table{
  height: 100%;
  width: 100%;
}

.task-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  padding: 24px;
}

.task-card {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  position: relative;
  min-height: 300px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.2s;
}

.task-card :deep(.el-card__body) {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.task-card:hover {
  border-color: #4F46E5;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.task-card-checkbox {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 18px;
  height: 18px;
  z-index: 5;
  cursor: pointer;
  accent-color: #4F46E5;
}

.task-card-thumbnail {
  position: relative;
  height: 180px;
  flex-shrink: 0;
  background: #E0E7FF;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.task-thumbnail-icon {
  color: #9CA3AF;
  display: flex;
  align-items: center;
  justify-content: center;
}

.task-thumbnail-audio {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.task-thumbnail-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.thumbnail-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 40px;
  color: white;
  z-index: 4;
  opacity: 0.8;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: 0.2s;
  cursor: pointer;
}

.task-card:hover .thumbnail-play-button {
  transform: translate(-50%, -50%) scale(1.1);
}

.task-progress-overlay {
  position: absolute;
  box-sizing: border-box;
  z-index: 3;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 12px;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.7));
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.task-progress-overlay :deep(.el-progress) {
  width: 100%;
}

.task-progress-overlay .status-text {
  font-size: 12px;
  color: #fff;
}

.task-card-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.task-card-name {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #1F2937;
}

.task-card-name > .meta-item {
  flex: 1;
  min-width: 0;
}

.task-card-name > .meta-item .value {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  word-break: normal;
}

.task-card-name-right{
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.task-id {
  font-size: 12px;
  color: #6B7280;
  font-family: monospace;
  display: flex;
  align-items: center;
  gap: 4px;
}

.task-card-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #4B5563;
}

.meta-icon {
  width: 16px;
  color: #9CA3AF;
  font-size: 14px;
  text-align: center;
}

.meta-item .value {
  color: #4B5563;
  word-break: break-all;
  font-weight: 500;
}

.meta-item.span-2 {
  grid-column: span 2;
}

.remark-item .value {
  min-height: 36px;
  max-height: 36px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 18px;
}

.task-card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  padding: 12px 16px;
  border-top: 1px solid #F3F4F6;
  margin-top: auto;
}




.queue-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  z-index: 5;
  backdrop-filter: blur(4px);
}

.status-line {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-info-icon {
  color: #4F46E5;
  cursor: pointer;
}

.task-card-thumbnail.thumbnail-clickable {
  cursor: pointer;
}

.task-card-thumbnail.thumbnail-clickable:hover {
  color: #4F46E5;
}

.task-preview-content {
  text-align: center;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #6B7280;
  padding: 24px 0;
}

.empty-task-list {
  padding: 120px 32px;
  text-align: center;
  color: #6B7280;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.empty-task-list :deep(.el-icon) {
  color: #9CA3AF;
}

.local-space {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 0;
}

.task-query-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid #F3F4F6;
  background: rgba(255, 255, 255, 0.8);
  margin-bottom: 0;
  margin-top: 0;
}



/* 骨架屏样式 */
.skeleton-card {
  min-height: 360px;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
}

.skeleton-thumbnail {
  width: 100%;
  height: 180px;
  background: #E0E7FF;
  border-radius: 0;
  margin-bottom: 0;
}

.skeleton-content {
  padding: 16px;
  margin-bottom: 0;
}

.skeleton-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #F3F4F6;
}

/* 状态标签样式调整 */
.task-card-name-right :deep(.el-tag) {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
}

/* 滚动条美化 */
.task-list-scrollable::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.task-list-scrollable::-webkit-scrollbar-track {
  background: transparent;
}

.task-list-scrollable::-webkit-scrollbar-thumb {
  background: #D1D5DB;
  border-radius: 3px;
}

.task-list-scrollable::-webkit-scrollbar-thumb:hover {
  background: #9CA3AF;
}
</style>
